import React, { useEffect, useRef, useState } from 'react';
import {
  ChevronRight,
  EditOutlined,
  ExpandLess,
  InfoOutlined,
  RestartAlt,
  SearchOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

// ---------------------------------------------------------------------------
// Look and feel
// ---------------------------------------------------------------------------
const PURPLE = '#512DA8';
const PURPLE_DARK = '#45259a';
const PURPLE_SOFT = '#f3effb';
const BORDER = '#e5e7eb';
const MUTED = '#6b7280';
const FAINT = '#9ca3af';

const AUDIENCE = {
  customer: { label: 'Customer', color: '#1d4ed8', bg: '#eff6ff' },
  admin: { label: 'Admin', color: '#6d28d9', bg: '#f5f3ff' },
  vendor: { label: 'Vendor', color: '#b45309', bg: '#fffbeb' },
};

// Inputs without floating labels: the dashboard's global CSS resets padding on
// every element, which breaks Material UI's floating label notch.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: '#fff',
    borderRadius: '8px',
    fontSize: 14,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: '#c4b5fd' },
    '&.Mui-focused fieldset': { borderColor: PURPLE, borderWidth: 1.5 },
  },
  '& .MuiOutlinedInput-input': { padding: '10px 12px' },
  '& .MuiInputBase-inputMultiline': { padding: 0, lineHeight: 1.6 },
  '& .MuiOutlinedInput-root.MuiInputBase-multiline': { padding: '10px 12px' },
};

const primaryBtnSx = {
  background: PURPLE,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  boxShadow: 'none',
  px: 2.5,
  '&:hover': { background: PURPLE_DARK, boxShadow: 'none' },
  '&.Mui-disabled': { background: '#e5e7eb', color: FAINT },
};

const ghostBtnSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  color: '#374151',
  border: `1px solid ${BORDER}`,
  background: '#fff',
  px: 2,
  '&:hover': { background: '#f9fafb', borderColor: '#d1d5db' },
  '&.Mui-disabled': { color: '#d1d5db', borderColor: '#f3f4f6' },
};

// Mirrors the server-side fill so previews show what the recipient will get.
const fillTemplate = (text, values) =>
  String(text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
    values[key] !== undefined && values[key] !== null && values[key] !== '' ? String(values[key]) : match
  );

// Inserts text at the cursor position of a textarea / input, falling back to the end.
const insertAtCursor = (ref, current, text) => {
  const el = ref?.current;
  if (!el || typeof el.selectionStart !== 'number') {
    return { value: `${current}${current && !/\s$/.test(current) ? ' ' : ''}${text}`, cursor: null };
  }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const value = current.slice(0, start) + text + current.slice(end);
  return { value, cursor: start + text.length };
};

const FieldLabel = ({ children, hint }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.75 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', letterSpacing: 0.3, textTransform: 'uppercase' }}>
      {children}
    </Typography>
    {hint && <Typography sx={{ fontSize: 12, color: FAINT }}>{hint}</Typography>}
  </Box>
);

const AudiencePill = ({ audience }) => {
  const a = AUDIENCE[audience] || AUDIENCE.customer;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '18px',
        px: 1,
        borderRadius: '10px',
        color: a.color,
        background: a.bg,
      }}
    >
      {a.label}
    </Box>
  );
};

const PlaceholderChips = ({ placeholders, onInsert }) => (
  <Box>
    <Typography sx={{ fontSize: 12, color: MUTED, mb: 0.75 }}>Click to insert at the cursor</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {placeholders.map((p) => (
        <Tooltip key={p.key} title={p.label} arrow>
          <Box
            component="button"
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onInsert(p.key)}
            sx={{
              border: `1px dashed #c4b5fd`,
              background: PURPLE_SOFT,
              color: PURPLE,
              borderRadius: '6px',
              px: 1,
              py: 0.4,
              fontSize: 12,
              fontFamily: 'monospace',
              cursor: 'pointer',
              '&:hover': { background: '#ebe4f9', borderStyle: 'solid' },
            }}
          >
            {p.key}
          </Box>
        </Tooltip>
      ))}
    </Box>
  </Box>
);

// Same look as the email the customer receives: logo header, paragraphs, details box, button
const SAMPLE_DETAILS = 'Enquiry: #1042 | Part: CC0402KRX7R9BB103 | Qty: 10,000';
const DETAILS_MARK = '@@DETAILS@@';

const EmailPreview = ({ subject, body, caption }) => {
  const paragraphs = String(body || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
  const signOff = /^(regards|warm regards|best regards|thank you|thanks|electrowiz admin)\b/i;
  const buttonBefore = paragraphs.length > 1 && signOff.test(paragraphs[paragraphs.length - 1]) ? paragraphs.length - 1 : -1;
  const button = (
    <Box key="button" sx={{ my: 1.5 }}>
      <Box component="span" sx={{ display: 'inline-block', background: PURPLE, color: '#fff', fontWeight: 600, fontSize: 13, px: 2, py: 0.9, borderRadius: '6px' }}>
        View details
      </Box>
      <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.5 }}>You will be asked to log in to see the details.</Typography>
    </Box>
  );
  const detailsBox = (
    <Box sx={{ background: '#f6f3fd', borderLeft: `4px solid ${PURPLE}`, borderRadius: '6px', px: 1.75, py: 1.25, mb: 1.5 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, color: PURPLE, mb: 0.5 }}>DETAILS</Typography>
      {SAMPLE_DETAILS.split(' | ').map((row) => {
        const [label, ...rest] = row.split(':');
        return (
          <Box key={row} sx={{ display: 'flex', gap: 1.5, fontSize: 12.5 }}>
            <Box sx={{ color: MUTED, minWidth: 54 }}>{label}</Box>
            <Box sx={{ fontWeight: 600, color: '#1f2937' }}>{rest.join(':').trim()}</Box>
          </Box>
        );
      })}
    </Box>
  );
  let first = true;

  return (
    <Box>
      <FieldLabel>Preview</FieldLabel>
      <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden', background: '#fff' }}>
        <Box sx={{ background: '#17142e', px: 2, py: 1.5, borderBottom: `3px solid ${PURPLE}` }}>
          <Box component="img" src="https://electrowiz.in/assets/logo.png" alt="ELECTROWIZ" sx={{ display: 'block', width: 150, maxWidth: '70%', height: 'auto', color: '#fff', fontWeight: 700 }} />
        </Box>
        <Box sx={{ px: 2, py: 1.75 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: '#111827', mb: 1.25 }}>
            {subject || <span style={{ color: FAINT, fontWeight: 400 }}>(no subject)</span>}
          </Typography>
          {!paragraphs.length && <Typography sx={{ color: FAINT, fontSize: 13 }}>(empty message)</Typography>}
          {paragraphs.map((p, i) => {
            const lead = i === buttonBefore ? button : null;
            if (p === DETAILS_MARK) return <React.Fragment key={i}>{lead}{detailsBox}</React.Fragment>;
            const isFirst = first;
            first = false;
            return (
              <React.Fragment key={i}>
                {lead}
                <Typography sx={{ whiteSpace: 'pre-wrap', color: isFirst ? '#111827' : '#374151', fontWeight: isFirst ? 600 : 400, lineHeight: 1.65, fontSize: 13, mb: 1.25 }}>
                  {p.split(DETAILS_MARK).join(SAMPLE_DETAILS)}
                </Typography>
              </React.Fragment>
            );
          })}
          {buttonBefore === -1 && paragraphs.length > 0 && button}
        </Box>
        <Box sx={{ px: 2, py: 1.25, background: '#faf9fd', borderTop: '1px solid #ece9f5' }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: '#1f2937' }}>Electrowiz</Typography>
          <Typography sx={{ fontSize: 10.5, color: MUTED }}>Wizard for all your electronics · electrowiz.in</Typography>
        </Box>
      </Box>
      {caption && <Typography sx={{ fontSize: 12, color: FAINT, mt: 1, lineHeight: 1.5 }}>{caption}</Typography>}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Template editor for one notification event
// ---------------------------------------------------------------------------
const EventTemplateEditor = ({ event, placeholders, defaultTemplate, onSaved }) => {
  const [subject, setSubject] = useState(event.subject || '');
  const [body, setBody] = useState(event.body || '');
  const [saving, setSaving] = useState(false);
  const [focused, setFocused] = useState('body');
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    setSubject(event.subject || '');
    setBody(event.body || '');
  }, [event.key, event.subject, event.body]);

  // The customer's real name and the real enquiry / order details are filled in when the email is sent
  const previewValues = {
    name: 'Customer name',
    title: event.name,
    message: DETAILS_MARK,
    link: 'https://electrowiz.in',
    website: 'https://electrowiz.in',
  };
  const tidy = (text) => String(text || '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  const defaultSubject = event.defaultSubject ?? defaultTemplate?.subject ?? '';
  const defaultBody = event.defaultBody ?? defaultTemplate?.body ?? '';

  const isDefault = subject.trim() === defaultSubject.trim() && body.trim() === defaultBody.trim();
  const dirty = subject !== (event.subject || '') || body !== (event.body || '');

  const insertPlaceholder = (key) => {
    const isSubject = focused === 'subject';
    const ref = isSubject ? subjectRef : bodyRef;
    const current = isSubject ? subject : body;
    const { value, cursor } = insertAtCursor(ref, current, key);
    if (isSubject) setSubject(value);
    else setBody(value);
    if (cursor !== null) {
      requestAnimationFrame(() => {
        ref.current?.focus();
        ref.current?.setSelectionRange(cursor, cursor);
      });
    }
  };

  const save = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and message cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const change = isDefault ? { key: event.key, resetTemplate: true } : { key: event.key, subject, body };
      await api.updateEmailEvents([change]);
      toast.success(`Template saved for "${event.name}"`);
      onSaved({ subject, body, customTemplate: !isDefault });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not save the template.');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setSubject(defaultSubject);
    setBody(defaultBody);
  };

  return (
    <Box
      sx={{
        background: '#fafafb',
        border: `1px solid ${BORDER}`,
        borderRadius: '12px',
        p: { xs: 2, md: 2.5 },
        mb: 2,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <FieldLabel>Subject</FieldLabel>
            <TextField
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              onFocus={() => setFocused('subject')}
              inputRef={subjectRef}
              placeholder="Email subject"
              sx={inputSx}
            />
          </Box>
          <Box>
            <FieldLabel>Message</FieldLabel>
            <TextField
              fullWidth
              multiline
              minRows={7}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onFocus={() => setFocused('body')}
              inputRef={bodyRef}
              placeholder="Write the email..."
              sx={inputSx}
            />
          </Box>

          <PlaceholderChips placeholders={placeholders} onInsert={insertPlaceholder} />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap', pt: 0.5 }}>
            <Button startIcon={<RestartAlt />} onClick={reset} disabled={isDefault || saving} sx={ghostBtnSx}>
              Reset to default
            </Button>
            <Button
              variant="contained"
              onClick={save}
              disabled={!dirty || saving}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
              sx={primaryBtnSx}
            >
              {saving ? 'Saving...' : 'Save template'}
            </Button>
          </Box>
        </Box>

        <Box>
          <EmailPreview
            subject={tidy(fillTemplate(subject, { ...previewValues, message: SAMPLE_DETAILS }))}
            body={tidy(fillTemplate(body, previewValues))}
            caption="Sample preview. When the email is sent, the customer's real name and the real enquiry or order details are filled in automatically."
          />
        </Box>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Email Notification Management page
// ---------------------------------------------------------------------------
const EmailNotifications = () => {
  const [modules, setModules] = useState([]);
  const [events, setEvents] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [activeModule, setActiveModule] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState({});
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await api.getEmailEvents();
        const data = res?.data?.data || {};
        setModules(data.modules || []);
        setEvents(data.events || []);
        setPlaceholders(data.placeholders || []);
        setDefaultTemplate(data.defaultTemplate || null);
      } catch (err) {
        const status = err?.response?.status;
        const message =
          status === 404
            ? 'The backend is running an older build without the email routes. Restart the backend (npm start in electrowiz-backend) and reload this page.'
            : err?.response?.data?.message || err?.message || 'Could not load the email notification settings.';
        setLoadError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const moduleName = (key) => modules.find((m) => m.key === key)?.name || '';
  const query = search.trim().toLowerCase();
  const matches = (e) =>
    !query ||
    [e.name, e.description, AUDIENCE[e.audience]?.label, moduleName(e.module)].some((v) =>
      String(v || '').toLowerCase().includes(query)
    );

  const searched = events.filter(matches);
  const visibleEvents = searched.filter((e) => activeModule === 'all' || e.module === activeModule);
  const statsFor = (key) => {
    const list = key === 'all' ? searched : searched.filter((e) => e.module === key);
    return { total: list.length, on: list.filter((e) => e.enabled).length };
  };

  const applyChanges = async (changes) => {
    if (!changes.length) return;
    const marks = {};
    changes.forEach((c) => {
      marks[c.key] = true;
    });
    setSaving((prev) => ({ ...prev, ...marks }));
    const previous = events;
    setEvents((prev) =>
      prev.map((e) => {
        const c = changes.find((x) => x.key === e.key);
        return c ? { ...e, enabled: c.enabled } : e;
      })
    );
    try {
      await api.updateEmailEvents(changes);
      if (changes.length > 1) {
        toast.success(`${changes[0].enabled ? 'Selected' : 'Deselected'} ${changes.length} emails`);
      }
    } catch (err) {
      setEvents(previous);
      toast.error(err?.response?.data?.message || 'Could not save the setting.');
    } finally {
      setSaving((prev) => {
        const next = { ...prev };
        changes.forEach((c) => delete next[c.key]);
        return next;
      });
    }
  };

  const toggleEvent = (event) => applyChanges([{ key: event.key, enabled: !event.enabled }]);
  const tickAll = (enabled) =>
    applyChanges(visibleEvents.filter((e) => e.enabled !== enabled).map((e) => ({ key: e.key, enabled })));

  const onTemplateSaved = (key, patch) => setEvents((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress sx={{ color: PURPLE }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1080 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: `1px solid ${BORDER}`, borderRadius: '14px' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>Email Notification Management</Typography>
          <Typography sx={{ color: '#b91c1c', fontSize: 14, mb: 2.5 }}>{loadError}</Typography>
          <Button onClick={() => window.location.reload()} sx={primaryBtnSx}>
            Reload
          </Button>
        </Paper>
      </Box>
    );
  }

  const panelTitle = activeModule === 'all' ? 'All modules' : moduleName(activeModule);
  const totalOn = events.filter((e) => e.enabled).length;
  const customCount = events.filter((e) => e.customTemplate).length;
  const visibleSelectedCount = visibleEvents.filter((e) => e.enabled).length;
  const allVisibleSelected = visibleEvents.length > 0 && visibleSelectedCount === visibleEvents.length;
  const someVisibleSelected = visibleSelectedCount > 0;

  const moduleRow = (key, name) => {
    const active = activeModule === key;
    const { total, on } = statsFor(key);
    const empty = total === 0;
    return (
      <Box
        key={key}
        onClick={() => {
          setActiveModule(key);
          setExpanded(null);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.75,
          py: 1.25,
          cursor: 'pointer',
          borderRadius: '8px',
          borderLeft: `3px solid ${active ? PURPLE : 'transparent'}`,
          background: active ? PURPLE_SOFT : 'transparent',
          transition: 'background 0.15s',
          '&:hover': { background: active ? PURPLE_SOFT : '#f9fafb' },
        }}
      >
        <Typography
          sx={{
            fontSize: 14.5,
            fontWeight: active ? 700 : 500,
            color: active ? PURPLE : empty && query ? '#d1d5db' : '#1f2937',
          }}
        >
          {name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, color: active ? PURPLE : FAINT, fontVariantNumeric: 'tabular-nums' }}>
            {empty ? 'No emails' : `${on} of ${total} selected`}
          </Typography>
          <ChevronRight sx={{ fontSize: 18, color: active ? PURPLE : '#d1d5db' }} />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1080 }}>
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
        {/* ---- Header ---- */}
        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 20, color: '#111827' }}>Email Notification Management</Typography>
              <Typography sx={{ fontSize: 13.5, color: MUTED, mt: 0.5 }}>
                You decide which emails Electrowiz sends automatically, and what each email says.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Chip size="small" label={`${totalOn} of ${events.length} emails selected`} sx={{ background: '#ecfdf5', color: '#047857', fontWeight: 600 }} />
                <Chip size="small" label={`${customCount} custom template${customCount === 1 ? '' : 's'}`} sx={{ background: PURPLE_SOFT, color: PURPLE, fontWeight: 600 }} />
              </Box>
            </Box>
            
          </Box>

          <Box
            sx={{
              mt: 2.5,
              display: 'flex',
              gap: 1.5,
              alignItems: 'flex-start',
              background: '#f8f7fd',
              border: '1px solid #e9e3f8',
              borderRadius: '10px',
              px: 2,
              py: 1.5,
            }}
          >
            <InfoOutlined sx={{ color: PURPLE, fontSize: 20, mt: '1px' }} />
            <Box sx={{ fontSize: 13.5, color: '#374151', lineHeight: 1.7 }}>
              <Box component="div"><b>1.</b> Choose a module below, for example BOM or Wizard.</Box>
              <Box component="div">
                <b>2.</b> <b>Select</b> the emails you want sent. A selected email is sent automatically. A deselected email is not sent.
              </Box>
              <Box component="div"><b>3.</b> Click <b>Edit template</b> to change what the email says, then <b>Save template</b>. The customer's name and the order or enquiry details are filled in automatically.</Box>
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Search emails, e.g. quotation, order, enquiry..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              mt: 2.5,
              ...inputSx,
              '& .MuiOutlinedInput-root': { ...inputSx['& .MuiOutlinedInput-root'], borderRadius: '24px', background: '#f9fafb' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: FAINT, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* ---- Modules ---- */}
        <Box sx={{ px: { xs: 1, md: 2 }, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', px: 1.75, mb: 0.75 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Module</Typography>
            <Typography sx={{ fontSize: 12, color: FAINT }}>{modules.length} modules</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 0.5 }}>
            {moduleRow('all', 'All modules')}
            {modules.map((m) => moduleRow(m.key, m.name))}
          </Box>
        </Box>

        {/* ---- Events of the chosen module ---- */}
        <Box sx={{ borderTop: `1px solid ${BORDER}`, px: { xs: 2, md: 3 }, py: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
              flexWrap: 'wrap',
              gap: 1,
              background: '#f9fafb',
              border: `1px solid ${BORDER}`,
              borderRadius: '10px',
              px: 1,
              py: 0.75,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={allVisibleSelected ? 'Deselect all' : 'Select all'} arrow>
                <span>
                  <Checkbox
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected && !allVisibleSelected}
                    disabled={!visibleEvents.length}
                    onChange={() => tickAll(!allVisibleSelected)}
                    sx={{ color: '#9ca3af', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: PURPLE } }}
                  />
                </span>
              </Tooltip>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>{panelTitle}</Typography>
                <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                  {visibleSelectedCount} of {visibleEvents.length} {visibleEvents.length === 1 ? 'email' : 'emails'} selected
                  {query ? ` · matching "${search.trim()}"` : ''}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, pr: 0.5 }}>
              <Button size="small" onClick={() => tickAll(true)} disabled={!visibleEvents.some((e) => !e.enabled)} sx={{ ...ghostBtnSx, py: 0.5 }}>
                Select all
              </Button>
              <Button
                size="small"
                onClick={() => tickAll(false)}
                disabled={!visibleEvents.some((e) => e.enabled)}
                sx={{ ...ghostBtnSx, py: 0.5, color: '#b91c1c', '&:hover': { background: '#fef2f2', borderColor: '#fecaca' } }}
              >
                Deselect all
              </Button>
            </Box>
          </Box>

          {!visibleEvents.length && (
            <Box sx={{ border: `1px dashed ${BORDER}`, borderRadius: '12px', py: 5, textAlign: 'center' }}>
              <Typography sx={{ color: FAINT, fontSize: 14 }}>
                {query ? 'No notification emails match your search.' : 'No automatic emails in this module yet.'}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {visibleEvents.map((e) => {
              const open = expanded === e.key;
              return (
                <Box
                  key={e.key}
                  sx={{
                    border: `1px solid ${open ? '#d8ccf1' : BORDER}`,
                    borderRadius: '12px',
                    background: '#fff',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: open ? '0 4px 14px rgba(81,45,168,0.08)' : 'none',
                    '&:hover': { borderColor: open ? '#d8ccf1' : '#d1d5db' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      pl: 1,
                      pr: 2,
                      py: 1.25,
                    }}
                  >
                    <Box
                      component="label"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, cursor: saving[e.key] ? 'wait' : 'pointer', flex: 1 }}
                    >
                      <Box sx={{ width: 42, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                        {saving[e.key] ? (
                          <CircularProgress size={20} sx={{ color: PURPLE }} />
                        ) : (
                          <Checkbox
                            checked={e.enabled}
                            onChange={() => toggleEvent(e)}
                            sx={{ color: '#9ca3af', '&.Mui-checked': { color: PURPLE } }}
                          />
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontWeight: 600, fontSize: 15, color: e.enabled ? '#111827' : '#6b7280' }}>{e.name}</Typography>
                          <AudiencePill audience={e.audience} />
                          {activeModule === 'all' && (
                            <Typography sx={{ fontSize: 12, color: FAINT }}>{moduleName(e.module)}</Typography>
                          )}
                          {e.customTemplate && (
                            <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: PURPLE, lineHeight: '18px', px: 1, borderRadius: '10px', background: PURPLE_SOFT }}>
                              Custom template
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>{e.description}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          px: 1.25,
                          lineHeight: '24px',
                          borderRadius: '12px',
                          whiteSpace: 'nowrap',
                          color: e.enabled ? '#047857' : '#6b7280',
                          background: e.enabled ? '#ecfdf5' : '#f3f4f6',
                          display: { xs: 'none', sm: 'inline-block' },
                        }}
                      >
                        {e.enabled ? 'Will be sent' : 'Not sent'}
                      </Box>
                      <Button
                        size="small"
                        onClick={() => setExpanded(open ? null : e.key)}
                        startIcon={open ? <ExpandLess /> : <EditOutlined sx={{ fontSize: 16 }} />}
                        sx={{ ...ghostBtnSx, py: 0.4, px: 1.5, fontSize: 13, color: open ? PURPLE : '#374151', borderColor: open ? '#d8ccf1' : BORDER }}
                      >
                        {open ? 'Close' : 'Edit template'}
                      </Button>
                    </Box>
                  </Box>

                  <Collapse in={open} unmountOnExit>
                    <Box sx={{ px: 2, pb: 0.5 }}>
                      <EventTemplateEditor
                        event={e}
                        placeholders={placeholders}
                        defaultTemplate={defaultTemplate}
                        onSaved={(patch) => onTemplateSaved(e.key, patch)}
                      />
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>

    </Box>
  );
};

export default EmailNotifications;
